// src/modules/app-views/leads/pages/Init/modals/ImportLeadsModal/helpers/parseFile.js
// File parser dispatcher for CSV, XLSX and JSON.
// Returns { headers, rows, columnsMeta } with columnsMeta[i] = { letter, header, index }.
// Uses dynamic import() so papaparse/xlsx only load when the modal opens.

const MAX_ROWS = 50000;
const MAX_SIZE_MB = 20;

export default async function parseFile(file) {
  if (!file) throw new Error('file_missing');

  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    throw new Error('file_too_large');
  }

  if (/\.xlsm$/i.test(file.name)) {
    throw new Error('xlsm_not_supported');
  }

  const ext = (file.name.split('.').pop() || '').toLowerCase();

  let parsed;
  if (ext === 'csv') parsed = await parseCsv(file);
  else if (ext === 'xlsx') parsed = await parseXlsx(file);
  else if (ext === 'json') parsed = await parseJson(file);
  else throw new Error('file_format_unsupported');

  if (parsed.rows.length > MAX_ROWS) {
    throw new Error('file_too_many_rows');
  }

  const columnsMeta = parsed.headers.map((header, index) => ({
    index,
    letter: indexToExcelLetter(index),
    header: header || '',
  }));

  return { ...parsed, columnsMeta };
}

async function parseCsv(file) {
  const Papa = (await import('papaparse')).default;
  const text = await file.text();
  return new Promise((resolve, reject) => {
    Papa.parse(text, {
      header: false,
      skipEmptyLines: true,
      complete: (result) => {
        const allRows = result.data || [];
        const [headerRow, ...dataRows] = allRows;
        resolve({ headers: headerRow || [], rows: dataRows });
      },
      error: () => reject(new Error('csv_parse_error')),
    });
  });
}

async function parseXlsx(file) {
  const XLSX = (await import('xlsx')).default;
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });
  const firstSheet = wb.SheetNames[0];
  if (!firstSheet) throw new Error('xlsx_no_sheets');
  const sheet = wb.Sheets[firstSheet];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
  const [headerRow, ...dataRows] = rows;
  return { headers: headerRow || [], rows: dataRows };
}

async function parseJson(file) {
  const text = await file.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('json_parse_error');
  }
  if (!Array.isArray(data)) throw new Error('json_must_be_array');
  if (data.length === 0) return { headers: [], rows: [] };
  if (typeof data[0] !== 'object' || data[0] === null || Array.isArray(data[0])) {
    throw new Error('json_must_be_array_of_objects');
  }

  const headers = Object.keys(data[0]);
  const rows = data.map(obj => headers.map(h => obj?.[h] ?? ''));
  return { headers, rows };
}

function indexToExcelLetter(index) {
  let i = index;
  let result = '';
  do {
    result = String.fromCharCode(65 + (i % 26)) + result;
    i = Math.floor(i / 26) - 1;
  } while (i >= 0);
  return result;
}
